import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Card, CardContent, Chip, Link, Stack, Typography } from '@mui/material';
import { cancelRentCarOrder } from '../../../api/endpoints/rentCar';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import type { PaymentResponse } from '../../../api/types/rentCar';
import type { ShoppingCart } from '../../../api/types/shoppingCart';
import { formatCurrency } from '../utils';

interface DocumentStepProps {
  shoppingCartId?: string;
  cart?: ShoppingCart | null;
  payment?: PaymentResponse;
  onStartOver: () => void;
}

export function DocumentStep({ shoppingCartId, cart, payment, onStartOver }: DocumentStepProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: () => cancelRentCarOrder({ shoppingCartId: shoppingCartId as string }),
    onSuccess: () => {
      enqueueSnackbar('Rezervasyon iptal edildi.', { variant: 'success' });
      setCancelDialogOpen(false);
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'İptal işlemi başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Rezervasyon Belgesi
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
            {cart?.isFinalized && <Chip color="success" size="small" label="Kesinleşti" />}
            {cart?.isPassengersLocked && <Chip size="small" label="Yolcular kilitli" />}
            {cart?.isBilled && <Chip size="small" label="Faturalandı" />}
          </Stack>
          <Typography variant="body2">Sepet ID: {shoppingCartId ?? '—'}</Typography>
          <Typography variant="body2">Kalan Tutar: {formatCurrency(cart?.remainingTotal)}</Typography>
          {payment?.continueUrl && (
            <Alert severity="info" sx={{ mt: 2 }}>
              3D Secure yönlendirmesi gerekiyor:{' '}
              <Link href={payment.continueUrl} target="_blank" rel="noreferrer">
                Ödemeyi tamamlamak için tıklayın
              </Link>
            </Alert>
          )}
        </CardContent>
      </Card>

      <ApiErrorAlert error={cancelMutation.error} sx={{ mb: 2 }} />

      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          color="error"
          disabled={!shoppingCartId || cancelMutation.isPending}
          onClick={() => setCancelDialogOpen(true)}
        >
          Rezervasyonu İptal Et
        </Button>
        <Button variant="text" onClick={onStartOver}>
          Yeni Arama Başlat
        </Button>
      </Stack>

      <ConfirmDialog
        open={cancelDialogOpen}
        title="Rezervasyonu iptal et"
        description="Bu işlem geri alınamaz. Devam etmek istiyor musunuz?"
        loading={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
        onCancel={() => setCancelDialogOpen(false)}
      />
    </Box>
  );
}
